import React, { useContext, useEffect, useState } from 'react';
import { BrowserRouter, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';

import { AngularServicesContext } from 'react-app';
import t from 'react-translate';

import { AnalyticsDashBoardType, AnalyticsPageType } from 'redux/schemas/app/analytics';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { fetchQuestionSet } from 'redux/actions/analytics';
import { useAppDispatch } from 'redux/store';
import { getCurrentCourse } from 'redux/selectors/course';
import { showPrePostTab } from 'redux/selectors/analytics';

import { createGridStyles, quarterSpacing } from 'styles/global_defaults/scaffolding';
import { textSmallFontSize } from 'styles/global_defaults/fonts';
import { gray6, warning } from 'styles/global_defaults/colors';

import { NvTab, NvResponsiveTabsDisplayType } from 'shared/components/nv-responsive-tabs';
import { NvResponsiveTabsRow } from 'shared/components/nv-responsive-tabs-row';
import NvIcon from 'shared/components/nv-icon';
import { resetQrveyValues } from 'analytics_dashboard/hooks/use-initialize-qrvey';
import AnalyticsDashboard, { AnalyticsContext } from './analytics-dashboard';
import PracticeAnalyticsTab from './practice-analytics-tab';
import QuizAndSurveyAnalyticsTab from './quiz-survey-analytics';
import QrveyWrapper from './qrvey-wrapper';
import QrveyPanelsWrapper from './qrvey-panels-wrapper';
import PrePostSurvey from './pre-post-survey';
import AnalyticsTabHeader from './analytics-tab-header';

const styles = css`
  .tab-container {
    position: sticky;
    top: 0;
    z-index: 2;
    border-bottom: solid 1px ${gray6};
  }
  .bottom-border-clickable {
    border-bottom: solid ${quarterSpacing}px ${warning}
  }
  .quiz-survey-dashboard {
    max-width: 1030px;
  }

  .practice-dropdown-title {
    align-items: center;
    display: flex;
    margin-left: 20px;

    .bs4-dropdown-menu {
      &.show {
        transform: translate3d(0%, 35px, 0px) !important;
        left: 20px !important;
      }
    }
  }
  .question-title {
    max-width: 800px;
  }
`;

enum TABS {
  OVERVIEW = 0,
  ACTIVITIES = 1,
  SKILLS = 2,
  QUIZ_SURVEY = 3,
  PRACTICE = 4,
  PRE_POST = 5,
}

const getCurrentActiveTab = (
  location,
  questionSetId,
  institutionHasSkilltags,
  showPrePostSurveyTab,
) => {
  let currentActiveTab = 0;

  const sections = [
    {
      url: '/activities',
      activeId: TABS.ACTIVITIES,
      display: true,
    },
    {
      url: '/skills',
      activeId: TABS.SKILLS,
      display: institutionHasSkilltags,
    },
    {
      url: '/pre-post-survey',
      activeId: TABS.PRE_POST,
      display: showPrePostSurveyTab,
    },
  ];

  sections.forEach((section) => {
    if (location?.hash?.endsWith(section.url) && section.display && !questionSetId) {
      currentActiveTab = section.activeId;
    } else if (questionSetId) {
      currentActiveTab = TABS.QUIZ_SURVEY;
    }
  });

  return currentActiveTab;
};

const CourseAnalytics = () => {
  const { $state, CurrentPermissionsManager } = useContext(AngularServicesContext);
  const { catalogId } = $state?.params;

  const institution = useSelector(getCurrentInstitution);
  const currentCourse = useSelector(getCurrentCourse);
  const userCourse = useSelector((state) => state.models.enrollments[currentCourse.userCourse]);
  const showPrePostSurveyTab = useSelector((state) => showPrePostTab(state, AnalyticsPageType.COURSE_ANALYTICS));
  const prePostSurveyConfigurationId = useSelector(state => state.app.analytics.currentPrePostConfigurationId);

  const institutionHasSkilltags = institution?.hasSkillTags;
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const [questionSetId, setQuestionSetId] = useState(null);
  const [activeTab, setActiveTab] = useState(getCurrentActiveTab(location, questionSetId, institutionHasSkilltags, showPrePostSurveyTab));
  const [selectedPage, setSelectedPage] = useState<string>(null);

  const isInstructor = CurrentPermissionsManager.isInstructor();
  const isReportAdmin = CurrentPermissionsManager.isReportAdmin();
  const showCohorts = (currentCourse?.isPrimary || (currentCourse?.isCohort && userCourse?.numCohortsReportAdminOrInstructorIn >= 1)) && (isReportAdmin || isInstructor);

  const BASE_URL = `/#!/courses/${catalogId}/analytics`;
  const selectTab = (id: number) => {
    setQuestionSetId(null);
    resetQrveyValues();
    setActiveTab(id);

    if (id === TABS.OVERVIEW) {
      window.location.href = BASE_URL;
    }
    if (id === TABS.ACTIVITIES) {
      window.location.href = `${BASE_URL}/activities`;
    }
    if (id === TABS.SKILLS && institutionHasSkilltags) {
      window.location.href = `${BASE_URL}/skills`;
    }

    if (id === TABS.PRE_POST) {
      window.location.href = `${BASE_URL}/pre-post-survey`;
    }
  };

  const tabs: NvTab[] = [
    {
      text: t.COURSE_ANALYTICS.OVERVIEW(),
      onClick: () => selectTab(TABS.OVERVIEW),
    },
    {
      text: t.COURSE_ANALYTICS.ACTIVITIES(),
      onClick: () => selectTab(TABS.ACTIVITIES),
    },
  ];

  if (institutionHasSkilltags) {
    tabs.push({
      text: t.COURSE_ANALYTICS.SKILLS(),
      onClick: () => selectTab(TABS.SKILLS),
    });
  }

  const tabStyles = {
    ...createGridStyles(1, 2),
    fontSize: `${textSmallFontSize}px`,
  };

  const onSelectQuestion = (id: number) => {
    setQuestionSetId(id);
    setActiveTab(TABS.QUIZ_SURVEY);
    history.push(`${BASE_URL}/quiz-and-survey/${id}`);
  };

  // This useEffect manages the opening and closing of the Practice Room Modal.
  // When the user opens the Practice Room Modal, we create a new instance of QRvey.
  // Since QRvey does not support handling multiple instances simultaneously, we ensure
  // that any existing QRvey instance is closed before opening a new one.
  useEffect(() => {
    // Check if the Practice Room Modal is being opened
    if ($state?.current?.name === 'practice-room-modal') {
      // Update the selected page to 'practice-room-modal'
      setSelectedPage($state?.current?.name);

      // Reset question set and activate the tab for the practice room
      setQuestionSetId(null);
      setActiveTab(TABS.PRACTICE); // Assuming 5 is the tab for the practice room
    } else if (selectedPage === 'practice-room-modal') {
      // If the Practice Room Modal is being closed, restore the previous state
      setSelectedPage(null);
      setActiveTab(null); // unset active tab
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [$state?.current?.name]); // Added dependencies for more accurate tracking

  useEffect(() => {
    if ($state.params?.questionId && $state.current?.name === 'quiz-survey') {
      setQuestionSetId($state.params?.questionId);
      setActiveTab(TABS.QUIZ_SURVEY);
    }
  }, [$state]);

  useEffect(() => {
    const currentActiveTab = getCurrentActiveTab(
      location,
      questionSetId,
      institutionHasSkilltags,
      showPrePostSurveyTab,
    );
    if (currentActiveTab !== activeTab) {
      setActiveTab(currentActiveTab);
    }
  }, [location, institutionHasSkilltags, activeTab, questionSetId, showPrePostSurveyTab]);

  // Fetch survey questions to determine whether to show pre-post survey tab
  useEffect(() => {
    dispatch(fetchQuestionSet({
      institutionId: institution?.id,
      catalogId,
      filterByType: 'Survey',
    }));
  }, [catalogId, dispatch, institution?.id]);

  // When the user directly access the pre-post or quiz-survey tab via URL,
  // it takes a moment to set the showPrePostSurveyTab state after API response.
  // To avoid temporarily rendering overview tab content
  const shouldShowOverviewTab = activeTab === TABS.OVERVIEW
    && (!location?.hash?.endsWith('/pre-post-survey')
    && !location?.hash?.endsWith('/quiz-and-survey'));

  return (
    <div css={styles}>
      <div className='d-flex justify-content-between bg-white pr-4 tab-container '>
        <div className='d-flex'>
          <NvResponsiveTabsRow
            style={tabStyles}
            defaultTabs={tabs}
            tabType={NvResponsiveTabsDisplayType.TEXT_ONLY}
            tabTextClass='card-title'
            selectedTabIndex={activeTab}
            revertActiveTab={questionSetId ? -1 : activeTab}
          />
          <PracticeAnalyticsTab catalogId={catalogId} />
          <QuizAndSurveyAnalyticsTab
            onSelect={onSelectQuestion}
            selectedId={questionSetId}
          />
          {showPrePostSurveyTab && (
            <AnalyticsTabHeader
              isActive={activeTab === TABS.PRE_POST}
              onClick={() => selectTab(TABS.PRE_POST)}
            >
              {t.COURSE_ANALYTICS.PRE_POST.TAB_HEADER()}
            </AnalyticsTabHeader>
          )}
        </div>
        {showCohorts && (
          <div className='d-flex align-items-center'>
            <a
              href={$state?.href('cohort-analytics', { catalogId })}
              className='d-flex text-primary font-weight-bold'
              aria-labelledby='cohort-analytics-link'
            >
              <NvIcon icon='data' size='smallest' />
              <span className='ml-1' id='cohort-analytics-link'>
                {t.COURSE_ADMIN_MEGA_MENU.COHORTS_ANALYTICS.LABEL()}
              </span>
            </a>
          </div>
        )}
      </div>

      {/* The conditionals are necessary to rerender the qrvey instance on click of the tabs */}

      {shouldShowOverviewTab && (
        <AnalyticsDashboard
          dashboardType={AnalyticsDashBoardType.COURSE}
          configKeyName='config_overview'
          pageIdKey='overview'
        >
          <QrveyWrapper />
        </AnalyticsDashboard>
      )}
      {activeTab === 1 && (
        <AnalyticsDashboard
          dashboardType={AnalyticsDashBoardType.COURSE}
          configKeyName='config_activities'
          pageIdKey='activities'
        >
          <QrveyWrapper />
        </AnalyticsDashboard>
      )}
      {activeTab === 2 && (
        <AnalyticsDashboard
          dashboardType={AnalyticsDashBoardType.COURSE}
          configKeyName='config_skills'
          pageIdKey='skills'
        >
          <QrveyWrapper />
        </AnalyticsDashboard>
      )}
      {questionSetId && (
        <div className='d-flex justify-content-center quiz-survey-dashboard mx-auto'>
          <AnalyticsDashboard
            dashboardType={AnalyticsDashBoardType.QUESTION_SET}
            configKeyName={`config_question_set_${questionSetId}`}
            pageIdKey='question_set'
            questionSetId={questionSetId}
          >
            <QrveyPanelsWrapper />
          </AnalyticsDashboard>
        </div>
      )}

      {showPrePostSurveyTab && activeTab === TABS.PRE_POST && (
        <AnalyticsContext.Provider
          value={{
            dashboardType: null,
            analyticsPageType: AnalyticsPageType.COURSE_ANALYTICS,
            catalogId,
            institutionId: institution?.id,
            prePostSurveyConfigurationId,
          }}
        >
          <PrePostSurvey />
        </AnalyticsContext.Provider>
      )}
    </div>
  );
};

export default CourseAnalytics;

export const CourseAnalyticsRoute = () => <BrowserRouter forceRefresh><CourseAnalytics /></BrowserRouter>;
